import { useState, useMemo } from "react";
import { ListPager } from "@/components/data-grid/ListPager";
import { usePagedRows } from "@/hooks/usePagedRows";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AirtableGrid,
  Column,
  GridToolbar,
} from "@/components/data-grid";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddCompanyDrawer } from "@/components/add/AddCompanyDrawer";
import { EmptyState } from "@/components/EmptyState";
import { ImportDialog } from "@/components/import/ImportDialog";
import { aggregateTrackRecord, formatClosingMultiple, type ClosingOutcome } from "@shared/closingMath";
import { formatDealValue } from "@/lib/formatDealValue";
import { ListShell } from "@/components/list/ListShell";
import { NameCell } from "@/components/list/NameCell";
import { ListTiles } from "@/components/list/ListTiles";
import { ChartChips, ALL_KEY } from "@/components/list/ChartChips";
import { clientTiles, clientStatusChips } from "@/components/list/tiles/clientTiles";

interface Client {
  id: string;
  name: string;
  industry: string | null;
  status: string | null;
  engagementType: string | null;
  stage: string | null;
  dealLead: string | null;
  dealLeadName: string | null;
  revenueRange: string | null;
  ebitdaRange: string | null;
  targetCloseDate: string | null;
  closing: {
    outcome: ClosingOutcome;
    closedOn: string;
    acquirerName: string | null;
    acquirerCompanyId: string | null;
    finalEv: number | null;
    ebitdaAtClose: number | null;
  } | null;
  daysInMarket: number | null;
  contactCount: number;
  lastActivityAt: string | null;
  holdReason: string | null;
  holdDate: string | null;
  holdRevisitDate: string | null;
  createdAt: string;
  // Project data
  projectId: string | null;
  projectName: string | null;
  overallProgress: number;
  currentPhase: string | null;
  nextMilestoneName: string | null;
  nextMilestoneDueDate: string | null;
}

type SortField = "name" | "status" | "phase" | "lastActivity";
type SortDirection = "asc" | "desc";

const ENGAGEMENT_OPTIONS = ["All", "M&A", "Capital Raise", "Debt Placement", "Restructuring", "ESOP"];
const INDUSTRY_OPTIONS = ["All", "Oilfield Services", "Construction", "Manufacturing", "HVAC", "Trucking & Logistics", "Concrete / Formwork", "Healthcare", "Technology", "Business Services"];

const getStatusStyle = (status: string | null) => {
  switch (status) {
    case "Active": return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900";
    case "Prospecting": return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-900";
    case "On Hold": return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900";
    case "Closed": return "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-900";
    default: return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900";
  }
};

const getEngagementStyle = (type: string | null) => {
  switch (type) {
    case "M&A": return "bg-navy-100 text-navy-700";
    case "Capital Raise": return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
    case "Debt Placement": return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
    case "Restructuring": return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
    case "ESOP": return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
    default: return "bg-muted text-muted-foreground";
  }
};

const getProgressColor = (p: number) => {
  if (p >= 75) return "bg-green-500";
  if (p >= 50) return "bg-yellow-500";
  if (p >= 25) return "bg-orange-500";
  return "bg-primary/30";
};

// Colour the client stage badge by stage keyword.
const getStageStyle = (stage: string | null) => {
  const s = (stage || "").toLowerCase();
  if (s.includes("closing")) return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900";
  if (s.includes("diligence")) return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-900";
  if (s.includes("loi")) return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-900";
  if (s.includes("market")) return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-900";
  if (s.includes("preparation")) return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900";
  if (s.includes("engagement")) return "bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-900";
  if (s.includes("prospect")) return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-900";
  return "bg-muted text-muted-foreground border-border";
};

const formatLastActivity = (date: string | null) => {
  if (!date) return "Never";
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function Clients() {
  const [, setLocation] = useLocation();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  // Closed engagements are reference material, not working pipeline, so they
  // start out of view. The Closed chip still reaches them, and matchesStatus
  // below counts a null status as Active so nothing becomes unreachable.
  const [statusFilter, setStatusFilter] = useState("Active");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [engagementFilter, setEngagementFilter] = useState("All");
  const [industryFilter, setIndustryFilter] = useState("All");

  // Stage filter from URL (?stage=Engaged) — used by dashboard Pipeline bar clicks
  const urlStage = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("stage");
  }, []);

  // Sorting
  const [sortField, setSortField] = useState<SortField>("lastActivity");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // UI
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/companies/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Dashboard "active deals"/funnels derive from clients — keep it in sync.
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/overview"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      toast.success("Client deleted");
    },
    onError: () => toast.error("Failed to delete client"),
  });

  const { data: clients = [], isLoading } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const response = await fetch("/api/clients", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch clients");
      return response.json();
    },
  });

  // Everything except status: the tiles and status chips count this, so they follow the
  // search and filters but still compare the statuses (spec R3).
  const scopedClients = useMemo(() => clients.filter((client) => {
    const matchesSearch = client.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEngagement = engagementFilter === "All" || client.engagementType === engagementFilter;
    const matchesIndustry = industryFilter === "All" || client.industry === industryFilter;
    const matchesStage = !urlStage || client.stage === urlStage;
    return matchesSearch && matchesEngagement && matchesIndustry && matchesStage;
  }), [clients, searchQuery, engagementFilter, industryFilter, urlStage]);

  const filteredClients = useMemo(() => {
    const result = scopedClients.filter((client) =>
      statusFilter === "All" || client.status === statusFilter || (!client.status && statusFilter === "Active"));

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "status":
          comparison = (a.status || "Active").localeCompare(b.status || "Active");
          break;
        case "phase":
          comparison = (a.overallProgress || 0) - (b.overallProgress || 0);
          break;
        case "lastActivity":
          const aDate = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
          const bDate = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
          comparison = aDate - bDate;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [scopedClients, statusFilter, sortField, sortDirection]);

  const activeFilterCount = [
    engagementFilter !== "All",
    industryFilter !== "All",
  ].filter(Boolean).length;

  const pagedClients = usePagedRows(filteredClients);

  const clientColumns: Column<Client>[] = useMemo(() => [
    {
      id: "name",
      header: "Company",
      cell: (client) => (
        <NameCell
          name={client.name}
          nameClassName="font-semibold text-foreground group-hover:text-primary-text transition-colors"
          after={client.status === "On Hold" ? (
            <Tooltip>
              <TooltipTrigger className="shrink-0">
                <Badge className={cn("text-[10px] px-1.5 py-0", getStatusStyle("On Hold"))}>Hold</Badge>
              </TooltipTrigger>
              <TooltipContent>
                {client.holdReason || "On hold"}
                {client.holdRevisitDate && ` • Revisit: ${new Date(client.holdRevisitDate).toLocaleDateString()}`}
              </TooltipContent>
            </Tooltip>
          ) : undefined}
          sub={client.industry || undefined}
          title={client.industry ? `${client.name} · ${client.industry}` : client.name}
        />
      ),
      width: "w-[260px]",
    },
    {
      id: "engagement",
      header: "Engagement",
      cell: (client) => (
        <span title={client.engagementType || undefined} className={cn("text-xs px-2 py-0.5 rounded-full font-medium", getEngagementStyle(client.engagementType))}>
          {client.engagementType || "—"}
        </span>
      ),
      width: "w-[130px]",
    },
    {
      id: "phase",
      header: "Progress",
      cell: (client) => {
        if (!client.projectId) {
          return <span className="text-xs text-muted-foreground/50">No project</span>;
        }
        return (
          <span className="flex items-center text-xs" title={`${client.currentPhase || "—"} ${client.overallProgress}%`}>
            <span className="truncate min-w-0">{`${client.currentPhase || "—"} ${client.overallProgress}%`}</span>
            <span className="inline-block shrink-0 ml-1.5 w-12 h-1 rounded-full bg-muted"><span className={cn("block h-1 rounded-full", getProgressColor(client.overallProgress))} style={{ width: `${client.overallProgress}%` }} /></span>
          </span>
        );
      },
      width: "w-[200px]",
    },
    {
      id: "stage",
      header: "Stage",
      cell: (client) => (
        client.stage ? (
          <Badge variant="outline" title={client.stage} className={cn("text-[10px] font-medium whitespace-nowrap", getStageStyle(client.stage))}>
            {client.stage}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground/50">—</span>
        )
      ),
      width: "w-[130px]",
    },
    {
      id: "status",
      header: "Status",
      cell: (client) => (
        // On a closed client the outcome is the useful part. A closed row
        // with no closing row is one of the deals closed before outcomes
        // were tracked, so say so plainly rather than showing nothing.
        <span title={client.status === "Closed" ? `Closed, ${client.closing ? client.closing.outcome : "outcome not recorded"}` : client.status || "Active"}>
          <Badge className={cn("text-[10px] font-medium w-fit", getStatusStyle(client.status))}>
            {client.status || "Active"}
          </Badge>
          {client.status === "Closed" && (
            <span className={cn("ml-1.5 text-[10px]", client.closing ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400")}>
              {client.closing ? client.closing.outcome : "not recorded"}
            </span>
          )}
        </span>
      ),
      width: "w-[170px]",
    },
    {
      id: "lastActivity",
      header: "Last Activity",
      cell: (client) => (
        <span title={formatLastActivity(client.lastActivityAt)} className={cn("text-sm", !client.lastActivityAt && "text-muted-foreground/50")}>
          {formatLastActivity(client.lastActivityAt)}
        </span>
      ),
      width: "w-[100px]",
    },
  ], []);

  const clientRowActions = (client: Client) => [
    { label: "View Details", icon: <ExternalLink className="w-4 h-4" />, onClick: () => setLocation(`/clients/${client.id}`) },
    { label: "Delete", icon: <Trash2 className="w-4 h-4" />, variant: "destructive" as const, onClick: () => setPendingDelete({ id: client.id, name: client.name }) },
  ];

  const statusChips = (
    <ChartChips
      ariaLabel="Status" chips={clientStatusChips(scopedClients)} allCount={scopedClients.length}
      value={statusFilter === "All" ? ALL_KEY : statusFilter}
      onChange={(k) => setStatusFilter(k === ALL_KEY ? "All" : k)}
    />
  );

  const filters = [
    {
      id: "engagement", label: "Engagement type", value: engagementFilter, onChange: setEngagementFilter, defaultValue: "All",
      options: ENGAGEMENT_OPTIONS.map((o) => ({ value: o, label: o })),
    },
    {
      id: "industry", label: "Industry", value: industryFilter, onChange: setIndustryFilter, defaultValue: "All",
      options: INDUSTRY_OPTIONS.map((o) => ({ value: o, label: o })),
    },
  ];

  // Track record, only while reviewing closed deals. Money is computed
  // strictly over sold deals whose terms were recorded, and the
  // denominator is stated so a partly-filled set cannot read as complete.
  const trackRecordStrip = statusFilter === "Closed" ? (() => {
    const t = aggregateTrackRecord(
      filteredClients
        .map((c) => c.closing)
        .filter((c): c is NonNullable<Client["closing"]> => !!c)
        .map((c) => ({ outcome: c.outcome, finalEv: c.finalEv, ebitdaAtClose: c.ebitdaAtClose }))
    );
    return (
      <div className="flex items-center gap-6 flex-wrap rounded-lg border bg-card px-3 py-1.5 text-sm">
        <span><span className="text-muted-foreground">Deals sold </span><b className="tabular-nums">{t.soldCount}</b></span>
        <span><span className="text-muted-foreground">Total value </span><b className="tabular-nums">{t.totalEv == null ? "n/a" : formatDealValue(String(t.totalEv))}</b></span>
        <span><span className="text-muted-foreground">Average multiple </span><b className="tabular-nums">{formatClosingMultiple(t.avgMultiple)}</b></span>
        {t.withTermsCount < t.soldCount && <span className="text-xs text-amber-700 dark:text-amber-400">{`${t.withTermsCount} of ${t.soldCount} sold deals have recorded terms`}</span>}
        {t.soldCount === 0 && <span className="text-xs text-muted-foreground">No sold deals with recorded outcomes yet.</span>}
      </div>
    );
  })() : null;

  const clientsEmptyState = (
    <EmptyState
      type="companies"
      title={activeFilterCount > 0 || statusFilter !== "All" ? "No clients match your filters" : undefined}
      description={activeFilterCount > 0 || statusFilter !== "All" ? "Try adjusting your search or filters." : undefined}
      onAction={() => setDrawerOpen(true)}
      showCreateButton={activeFilterCount === 0 && statusFilter === "All"}
    />
  );

  return (
    <ListShell
      title="Clients"
      tiles={<ListTiles tiles={clientTiles(scopedClients, new Date())} />}
      toolbar={<GridToolbar density="compact" searchValue={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder="Search clients..."
        filters={filters} chips={statusChips}
        actions={<Button variant="outline" size="sm" className="h-8" onClick={() => setImportOpen(true)}>Import</Button>}
        primaryAction={<Button size="sm" className="h-8 gap-1.5" onClick={() => setDrawerOpen(true)}><Plus className="w-4 h-4" /> Add Client</Button>} />}
      above={trackRecordStrip}
      footer={<ListPager density="compact" shown={pagedClients.rows.length} total={pagedClients.total} noun={["client", "clients"]}
        page={pagedClients.page} totalPages={pagedClients.totalPages} pageSize={pagedClients.pageSize} onPageSizeChange={pagedClients.setPageSize}
        hasPrev={pagedClients.hasPrev} hasNext={pagedClients.hasNext} onPrev={pagedClients.prev} onNext={pagedClients.next} testIdPrefix="clients" />}
      after={<>
      <AddCompanyDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        preselectedCategory="clients"
      />
      <ImportDialog type="clients" open={importOpen} onOpenChange={setImportOpen} />
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(next) => !next && !deleteMutation.isPending && setPendingDelete(null)}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : ""}
        description="This archives the client so it no longer shows in the active list. Contacts, deals, and activity history stay intact. You can restore from the Archived page in Settings."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteMutation.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
      </>}
    >
      <AirtableGrid density="compact" scroll data={pagedClients.rows} columns={clientColumns} getRowId={(c) => c.id}
        onRowClick={(c) => setLocation(`/clients/${c.id}`)} getRowActions={clientRowActions} loading={isLoading}
        emptyState={clientsEmptyState} />
    </ListShell>
  );
}
